import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, Profile } from '../../lib/supabase';
import { useTrades } from '../../hooks/useTrades';
import { TradeBadge } from '../shared/TradeBadge';
import { ContractorProfileModal } from './ContractorProfileModal';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../shared/Pagination';
import { ContractorCardSkeleton } from '../shared/LoadingSkeleton';
import { EmptyState } from '../shared/EmptyState';
import {
  Search,
  SlidersHorizontal,
  CheckCircle2,
  Shield,
  MapPin,
  Star,
  Briefcase,
  UserPlus,
  MessageSquare,
  Heart,
  X,
  Award,
  Filter,
  Users
} from 'lucide-react';

export const TradesDirectory: React.FC = () => {
  const { profile } = useAuth();
  const { trades } = useTrades();
  const [contractors, setContractors] = useState<Profile[]>([]);
  const [savedContractorIds, setSavedContractorIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState<Profile | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    trades: [] as string[],
    location: '',
    verifiedOnly: true,
    minRating: 0,
    minJobsCompleted: 0,
    hasInsurance: false,
    hasCertifications: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'rating' | 'jobs_completed' | 'created_at'>('rating');

  const pagination = usePagination(contractors, { pageSize: 12 });

  useEffect(() => {
    loadContractors();
    loadSavedContractors();
  }, []);

  useEffect(() => {
    loadContractors();
  }, [filters, sortBy, searchQuery]);

  const loadSavedContractors = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('saved_trades')
        .select('contractor_id')
        .eq('gc_id', profile.id);

      if (error) throw error;
      setSavedContractorIds(new Set(data?.map(s => s.contractor_id) || []));
    } catch (error) {
      console.error('Error loading saved contractors:', error);
    }
  };

  const loadContractors = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('profiles')
        .select('*')
        .eq('role', 'contractor')
        .order(sortBy, { ascending: false });

      if (filters.verifiedOnly) {
        query = query.eq('verification_status', 'verified');
      }

      if (filters.trades.length > 0) {
        query = query.contains('trades', filters.trades);
      }

      if (filters.location) {
        query = query.ilike('location', `%${filters.location}%`);
      }

      if (filters.minRating > 0) {
        query = query.gte('rating', filters.minRating);
      }

      if (filters.minJobsCompleted > 0) {
        query = query.gte('jobs_completed', filters.minJobsCompleted);
      }

      const { data, error } = await query;

      if (error) throw error;

      let filteredData = data || [];

      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        filteredData = filteredData.filter(c =>
          c.full_name?.toLowerCase().includes(search) ||
          c.company_name?.toLowerCase().includes(search) ||
          c.location?.toLowerCase().includes(search)
        );
      }

      if (filters.hasInsurance) {
        const contractorsWithInsurance = await Promise.all(
          filteredData.map(async (c) => {
            const { count } = await supabase
              .from('insurance_policies')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', c.id)
              .eq('status', 'active');
            return count && count > 0 ? c : null;
          })
        );
        filteredData = contractorsWithInsurance.filter(c => c !== null) as Profile[];
      }

      if (filters.hasCertifications) {
        const contractorsWithCerts = await Promise.all(
          filteredData.map(async (c) => {
            const { count } = await supabase
              .from('certifications')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', c.id)
              .eq('verified', true);
            return count && count > 0 ? c : null;
          })
        );
        filteredData = contractorsWithCerts.filter(c => c !== null) as Profile[];
      }

      setContractors(filteredData);
    } catch (error) {
      console.error('Error loading contractors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRoster = async (contractorId: string) => {
    if (!profile) return;

    try {
      if (savedContractorIds.has(contractorId)) {
        const { error } = await supabase
          .from('saved_trades')
          .delete()
          .eq('gc_id', profile.id)
          .eq('contractor_id', contractorId);

        if (error) throw error;
        setSavedContractorIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(contractorId);
          return newSet;
        });
      } else {
        const { error } = await supabase
          .from('saved_trades')
          .insert({
            gc_id: profile.id,
            contractor_id: contractorId,
          });

        if (error) throw error;
        setSavedContractorIds(prev => new Set(prev).add(contractorId));
      }
    } catch (error: any) {
      console.error('Error updating roster:', error);
      alert('Failed to update roster');
    }
  };

  const toggleTradeFilter = (trade: string) => {
    setFilters(prev => ({
      ...prev,
      trades: prev.trades.includes(trade)
        ? prev.trades.filter(t => t !== trade)
        : [...prev.trades, trade]
    }));
  };

  const clearFilters = () => {
    setFilters({
      trades: [],
      location: '',
      verifiedOnly: true,
      minRating: 0,
      minJobsCompleted: 0,
      hasInsurance: false,
      hasCertifications: false,
    });
    setSearchQuery('');
  };

  const activeFiltersCount = [
    filters.trades.length > 0,
    filters.location !== '',
    filters.minRating > 0,
    filters.minJobsCompleted > 0,
    filters.hasInsurance,
    filters.hasCertifications,
  ].filter(Boolean).length;


  return (
    <>
      <div className="space-y-6">
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900 rounded-2xl p-8 overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="relative">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Find Trades</h1>
                <p className="text-slate-300">{contractors.length} verified contractors available</p>
              </div>

              <div className="flex items-center space-x-2 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search contractors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-white/20 bg-white/10 backdrop-blur-sm rounded-lg text-white placeholder:text-slate-300 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/50 outline-none"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all relative"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-600 text-white text-xs rounded-full flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-slate-600 hover:text-slate-900 flex items-center"
              >
                <X className="w-4 h-4 mr-1" />
                Clear all
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Trade Types</label>
                <div className="flex flex-wrap gap-2">
                  {trades.map((trade) => (
                    <button
                      key={trade.id}
                      onClick={() => toggleTradeFilter(trade.name)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        filters.trades.includes(trade.name)
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {trade.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                  <input
                    type="text"
                    placeholder="City or ZIP"
                    value={filters.location}
                    onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Min Rating</label>
                  <select
                    value={filters.minRating}
                    onChange={(e) => setFilters({ ...filters, minRating: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  >
                    <option value="0">Any rating</option>
                    <option value="3">3.0+</option>
                    <option value="3.5">3.5+</option>
                    <option value="4">4.0+</option>
                    <option value="4.5">4.5+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Min Jobs Completed</label>
                  <select
                    value={filters.minJobsCompleted}
                    onChange={(e) => setFilters({ ...filters, minJobsCompleted: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  >
                    <option value="0">Any amount</option>
                    <option value="5">5+</option>
                    <option value="10">10+</option>
                    <option value="25">25+</option>
                    <option value="50">50+</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={filters.verifiedOnly}
                    onChange={(e) => setFilters({ ...filters, verifiedOnly: e.target.checked })}
                    className="w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900"
                  />
                  <span className="text-sm text-slate-700">Verified only</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={filters.hasInsurance}
                    onChange={(e) => setFilters({ ...filters, hasInsurance: e.target.checked })}
                    className="w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900"
                  />
                  <span className="text-sm text-slate-700">Has active insurance</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={filters.hasCertifications}
                    onChange={(e) => setFilters({ ...filters, hasCertifications: e.target.checked })}
                    className="w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900"
                  />
                  <span className="text-sm text-slate-700">Has certifications</span>
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {savedContractorIds.size > 0 && (
              <span className="flex items-center">
                <Heart className="w-4 h-4 mr-1 text-red-500" />
                {savedContractorIds.size} saved to roster
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-600">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
            >
              <option value="rating">Highest rated</option>
              <option value="jobs_completed">Most experienced</option>
              <option value="created_at">Newest members</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <ContractorCardSkeleton key={i} />
            ))}
          </div>
        ) : contractors.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No contractors found"
            description="Try adjusting your filters to see more results."
            action={activeFiltersCount > 0 ? {
              label: 'Clear All Filters',
              onClick: clearFilters,
            } : undefined}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pagination.paginatedItems.map((contractor) => {
              const isSaved = savedContractorIds.has(contractor.id);
              return (
                <div key={contractor.id} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-xl hover:border-amber-300 transition-all group hover:scale-[1.02]">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-semibold text-slate-700">
                          {contractor.full_name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <button
                            onClick={() => setSelectedContractor(contractor)}
                            className="font-bold text-slate-900 truncate hover:text-amber-600 transition-colors text-left"
                          >
                            {contractor.full_name}
                          </button>
                          {contractor.verification_status === 'verified' && (
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" title="Verified" />
                          )}
                        </div>
                        {contractor.company_name && (
                          <p className="text-xs text-slate-500 truncate">{contractor.company_name}</p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(contractor.trades && contractor.trades.length > 0) ? (
                            contractor.trades.slice(0, 2).map((trade) => (
                              <TradeBadge key={trade} trade={trade} size="sm" />
                            ))
                          ) : contractor.trade_type ? (
                            <TradeBadge trade={contractor.trade_type} size="sm" />
                          ) : null}
                          {contractor.trades && contractor.trades.length > 2 && (
                            <span className="text-xs text-slate-500 px-2 py-0.5">+{contractor.trades.length - 2}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleRoster(contractor.id)}
                      className="flex-shrink-0 ml-2"
                    >
                      <Heart className={`w-5 h-5 transition-colors ${
                        isSaved ? 'fill-red-500 text-red-500' : 'text-slate-300 hover:text-red-500'
                      }`} />
                    </button>
                  </div>

                  {contractor.bio && (
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">{contractor.bio}</p>
                  )}

                  <div className="space-y-2 mb-4">
                    {contractor.location && (
                      <div className="flex items-center text-sm text-slate-600">
                        <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{contractor.location}</span>
                      </div>
                    )}
                    <div className="flex items-center text-sm text-slate-600">
                      <Star className="w-4 h-4 mr-2 text-amber-500 flex-shrink-0" />
                      <span className="font-medium text-slate-900">{contractor.rating.toFixed(1)}</span>
                      <span className="mx-1 text-slate-400">•</span>
                      <span>{contractor.jobs_completed} jobs</span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedContractor(contractor)}
                      className="flex-1 px-3 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-medium text-sm rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                    >
                      <Award className="w-4 h-4 mr-1.5" />
                      View Profile
                    </button>
                    <button className="px-3 py-2 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg transition-all shadow-md hover:shadow-lg">
                      <MessageSquare className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>

            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.goToPage}
              hasNextPage={pagination.hasNextPage}
              hasPreviousPage={pagination.hasPreviousPage}
              getPageNumbers={pagination.getPageNumbers}
              startIndex={pagination.startIndex}
              endIndex={pagination.endIndex}
              totalItems={pagination.totalItems}
            />
          </>
        )}
      </div>

      {selectedContractor && (
        <ContractorProfileModal
          contractor={selectedContractor}
          onClose={() => setSelectedContractor(null)}
          isSaved={savedContractorIds.has(selectedContractor.id)}
          onToggleSave={() => handleToggleRoster(selectedContractor.id)}
        />
      )}
    </>
  );
};
